import React, { useMemo } from 'react';
import type { ReportData } from '../../../support/aiEraReport/report/types';
import { aiEraReportToModel, AI_ERA_REPORT_CONFIG } from '../../../support/aiEraReport/toReportModel';
import { isReportConfig, type ReportConfig } from '../../../support/reportView/types';
import ReportRenderer from '../report/ReportRenderer';

/**
 * Convenience wrapper for the AI-Era FAIR report: it maps the engine's
 * `ReportData` into the engine-agnostic `ReportModel` and renders it through
 * the standard, config-driven `ReportRenderer`. The report is NOT hardcoded
 * here — the composition comes from `config` (the admin-authored
 * `assessment.metadata.reportLayout` when present) and falls back to the seeded
 * `AI_ERA_REPORT_CONFIG`. Any other engine can reuse `ReportRenderer` the same
 * way by producing a `ReportModel` + a `ReportConfig`.
 */

type Props = {
  report: ReportData;
  /** Admin-authored composition (from `assessment.metadata.reportLayout`); falls
   *  back to the seeded AI-Era default when absent or malformed. */
  config?: unknown;
  dark?: boolean;
  actions?: React.ReactNode;
};

const AiEraReportView: React.FC<Props> = ({ report, config, dark, actions }) => {
  const model = useMemo(() => aiEraReportToModel(report), [report]);
  const resolvedConfig: ReportConfig = isReportConfig(config) ? config : AI_ERA_REPORT_CONFIG;
  return <ReportRenderer config={resolvedConfig} model={model} dark={dark} actions={actions} />;
};

export default AiEraReportView;
